import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { composeAddress, makeApi } from '../api/client';
import type {
  AdminCustomerSummary,
  AdminProject,
  ClaimStatus,
  MeAddress,
} from '../api/client';

interface Detail {
  meta: AdminCustomerSummary;
  addresses: (Omit<MeAddress, 'projects'> & { claimStatus: ClaimStatus })[];
  links: { projectId: string; addressId: string; linkedAt: string }[];
  projects: AdminProject[];
}

export function AdminCustomerDetailPage(): JSX.Element {
  const { sub = '' } = useParams<{ sub: string }>();
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const navigate = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.adminGetCustomer(sub);
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  }, [api, sub]);

  useEffect(() => {
    load();
  }, [load]);

  async function call(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  if (!data) return <div className="text-gray-500">Loading…</div>;
  const m = data.meta;
  const projectsByAddress = new Map<string, AdminProject[]>();
  for (const link of data.links) {
    const proj = data.projects.find((p) => p.pid === link.projectId);
    if (!proj) continue;
    const arr = projectsByAddress.get(link.addressId) ?? [];
    arr.push(proj);
    projectsByAddress.set(link.addressId, arr);
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/customers" className="text-sm text-red-400 hover:text-red-300 hover:underline">
          &larr; Back
        </Link>
      </div>

      <div className="card space-y-3">
        <div>
          <h1 className="text-xl font-semibold text-white">{m.email}</h1>
          <div className="text-xs text-gray-500">sub: {m.sub}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="badge bg-gray-800 text-gray-300 border border-gray-700">{m.accountStatus}</span>
          <span className="badge bg-gray-800 text-gray-300 border border-gray-700">tier: {m.accountTier}</span>
          {m.flagged && <span className="badge bg-red-900/60 text-red-300 border border-red-700/50">flagged</span>}
        </div>
        {m.flagReasons.length > 0 && (
          <div className="text-xs text-gray-500">Reasons: {m.flagReasons.join(', ')}</div>
        )}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-800">
          {m.accountStatus === 'disabled' ? (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.adminEnable(sub))}>
              Enable
            </button>
          ) : (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.adminDisable(sub))}>
              Disable
            </button>
          )}
          {m.accountTier === 'standard' ? (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.adminUpgrade(sub))}>
              Upgrade to advanced
            </button>
          ) : (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.adminDowngrade(sub))}>
              Downgrade to standard
            </button>
          )}
          {m.flagged ? (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.adminUnflag(sub))}>
              Clear flag
            </button>
          ) : (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.adminFlag(sub))}>
              Flag for review
            </button>
          )}
          <button
            className="btn-danger"
            disabled={busy}
            onClick={async () => {
              if (!confirm(
                'Permanently deregister this account?\n\n' +
                'All customer data will be deleted and their issues anonymised. ' +
                'A backup will be saved to S3 before deletion.',
              )) return;
              setBusy(true);
              setError(null);
              try {
                const res = await api.adminDeregister(sub);
                alert(
                  `Account deregistered.\n\nBackup saved to S3:\n${res.backupKey}\n\n` +
                  `(Bucket: ${import.meta.env.VITE_ATTACHMENTS_BUCKET ?? 'see AWS console'})`,
                );
                navigate('/admin/customers');
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Deregister failed');
                setBusy(false);
              }
            }}
          >
            Deregister
          </button>
        </div>
      </div>

      <div className="card space-y-3">
        <h2 className="font-semibold text-white">Addresses</h2>
        {data.addresses.length === 0 && <div className="text-gray-500 text-sm">None</div>}
        {data.addresses.map((a) => (
          <div key={a.addressId} className="border border-gray-700 rounded p-3 bg-gray-800/30">
            <div className="flex justify-between items-start gap-2">
              <div>
                <div className="font-medium text-white">{composeAddress(a)}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  <span className="badge bg-gray-800 text-gray-300 border border-gray-700">{a.claimStatus}</span>
                  {(projectsByAddress.get(a.addressId) ?? []).map((p) => (
                    <span key={p.pid} className="badge bg-gray-800 text-gray-300 border border-gray-700">
                      {p.displayName} ({p.projectReferenceNumber})
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {a.claimStatus === 'contested' && (
                  <button
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() =>
                      call(() => api.adminPromoteClaim(sub, a.addressId, true))
                    }
                  >
                    Promote (force)
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card space-y-2">
        <h2 className="font-semibold text-white">Project links</h2>
        {data.links.length === 0 && (
          <div className="text-gray-500 text-sm">No active project links</div>
        )}
        {data.links.map((l) => {
          const proj = data.projects.find((p) => p.pid === l.projectId);
          const addr = data.addresses.find((a) => a.addressId === l.addressId);
          return (
          <div
            key={`${l.projectId}|${l.addressId}`}
            className="flex justify-between items-center border-t border-gray-800 pt-2 first:border-0 first:pt-0"
          >
            <div className="text-sm">
              <div className="font-medium text-white">
                {proj ? `${proj.displayName} (${proj.projectReferenceNumber})` : l.projectId}
              </div>
              <div className="text-xs text-gray-400">
                {addr ? composeAddress(addr) : l.addressId}
              </div>
              <div className="text-xs text-gray-600">
                linked {new Date(l.linkedAt).toLocaleString()}
              </div>
            </div>
            <button
              className="btn-danger text-sm"
              disabled={busy}
              onClick={() => call(() => api.adminDeleteLink(sub, l.projectId, l.addressId))}
            >
              Remove link
            </button>
          </div>
          );
        })}
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}
    </div>
  );
}
